import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Users, Download, Edit, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReporteUsuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      const [tecnicosRes, clientesRes] = await Promise.all([
        axios.get(`${API}/admin/tecnicos`),
        axios.get(`${API}/admin/clientes`)
      ]);
      
      const admin = { 
        id: 'admin', 
        email: 'admin@surnexa.com', 
        nombre_completo: 'Administrador Surnexa',
        rol: 'administrador',
        telefono: '-',
        created_at: new Date()
      };
      
      setUsuarios([admin, ...tecnicosRes.data, ...clientesRes.data]);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (usuarioId, usuarioNombre, usuarioRol) => {
    if (usuarioRol === 'administrador') {
      alert('No se puede eliminar el usuario administrador');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar al usuario "${usuarioNombre}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = usuarioRol === 'cliente' 
        ? `/admin/clientes/${usuarioId}`
        : `/admin/usuarios/${usuarioId}`;
      
      await axios.delete(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsuarios(usuarios.filter(u => u.id !== usuarioId));
      setDeleteError('');
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleEdit = (usuario) => {
    if (usuario.rol === 'administrador') {
      alert('No se puede editar el usuario administrador desde aquí');
      return;
    }
    
    if (usuario.rol === 'cliente') {
      navigate(`/admin/clientes/${usuario.id}/editar`);
    } else {
      // Para técnicos u otros roles, podrías agregar otra ruta
      navigate(`/admin/usuarios/${usuario.id}/editar`);
    }
  };

  const exportarExcel = () => {
    const data = usuarios.map(u => ({
      'Nombre': u.nombre_completo,
      'Email': u.email,
      'Rol': u.rol,
      'Teléfono': u.telefono || 'N/A',
      'RUT': u.rut || 'N/A',
      'Dirección': u.direccion || 'N/A',
      'Estado': u.estado || 'activo',
      'Fecha Registro': new Date(u.created_at).toLocaleDateString('es-ES')
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');
    
    const filename = `usuarios_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const getRolColor = (rol) => {
    const colores = {
      'administrador': 'bg-red-100 text-red-800',
      'tecnico': 'bg-green-100 text-green-800',
      'cliente': 'bg-purple-100 text-purple-800'
    };
    return colores[rol] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>

        {deleteError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{deleteError}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="text-2xl flex items-center gap-2">
                <Users className="h-6 w-6" />
                Reporte de Usuarios ({usuarios.length})
              </CardTitle>
              <Button onClick={exportarExcel} variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Exportar Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Nombre</th>
                    <th className="text-left p-3 font-semibold">Email</th>
                    <th className="text-left p-3 font-semibold">Rol</th>
                    <th className="text-left p-3 font-semibold">Teléfono</th>
                    <th className="text-left p-3 font-semibold">RUT</th>
                    <th className="text-left p-3 font-semibold">Estado</th>
                    <th className="text-left p-3 font-semibold">Fecha Registro</th>
                    <th className="text-right p-3 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{usuario.nombre_completo}</td>
                      <td className="p-3 text-sm">{usuario.email}</td>
                      <td className="p-3">
                        <Badge className={getRolColor(usuario.rol)}>
                          {usuario.rol}
                        </Badge>
                      </td>
                      <td className="p-3 text-sm">{usuario.telefono || '-'}</td>
                      <td className="p-3 text-sm">{usuario.rut || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          usuario.estado === 'activo' || !usuario.estado
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {usuario.estado || 'activo'}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(usuario.created_at).toLocaleDateString('es-ES')}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(usuario)}
                            disabled={usuario.rol === 'administrador'}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(usuario.id, usuario.nombre_completo, usuario.rol)}
                            disabled={usuario.rol === 'administrador'}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReporteUsuarios;
